/**
 * PA loyalty single-login mock (MOVMO-541) + wire-authoritative rework
 * (MOVMO-567) + real backend wiring (MOVMO-542).
 *
 * ---------------------------------------------------------------------
 * STATE-MACHINE SEAM: the WIRE is authoritative for whether Mabuhay Miles
 * shows as linked and for the member_number/tier_status/points rendered.
 * Wire sources are now THREE call sites, all decoding the identical
 * {recognized, profile?, preferences?, loyalty?} shape because each is a
 * server-side resolve result: PA's GET /me response
 * (services/demoAirlineApi.ts, DemoAirlineLoyaltyClaims), AND — as of
 * MOVMO-542 — the `movmo` field on a POST/DELETE .../loyalty/link response
 * (DemoAirlineLoyaltyLinkResponse), applied via
 * hooks/linkedTravelerStore.ts's applyWireTraveler() into the SAME
 * sessionStorage-backed store the /me read feeds. `loyalty` is
 * present only when the traveler granted `loyalty.read` to PA in Movmo. See
 * deriveWireLoyaltyLink() below and hooks/useLoyaltySession.ts, which is
 * the ONLY thing allowed to decide sessionActive/link for the header chip
 * and trip-type toggle.
 *
 * MOCK_LOYALTY_MEMBER stays for MODAL DISPLAY COPY ONLY (the default
 * pre-filled member-number field, and legacy toast-flag decoding for the
 * loyalty-in-consent OAuth-redirect path below) — never fed into the
 * resting linked/unlinked display. A traveler who runs the on-site
 * late-link ceremony (LoyaltyLinkModal, now backed by the real
 * POST .../loyalty/link call) or lands here via a loyalty-in-consent OAuth
 * redirect sees a truthful "here's what just happened" toast built from
 * the REAL response's wire loyalty group, but the header/trip-selector
 * only show the member chip once the traveler's Movmo loyalty.read grant
 * actually returns a program — same as any other loyalty state change.
 * ---------------------------------------------------------------------
 */

import { DemoAirlineLoyaltyClaims } from "./demoAirlineApi";

export interface LoyaltyLinkRecord {
    memberNumber: string;
    tier: string;
    milesBalance: number;
    linkedAt: string;
    path: "linked" | "enrolled";
    importConsent: boolean;
}

export const MOCK_LOYALTY_MEMBER = {
    memberNumber: "PR-1978-8842",
    tier: "Elite",
    milesBalance: 128540,
};

export function formatMiles(n: number): string {
    return n.toLocaleString("en-US");
}

// "b•••@movmo.io" — first character + masked local part + full domain, so
// the "we sent a code to..." copy still looks like a real destination.
export function maskEmail(email?: string): string {
    const at = email?.indexOf("@") ?? -1;
    if (!email || at <= 0) return "your email on file";
    return `${email[0]}•••${email.slice(at)}`;
}

export function newEnrolledMemberNumber(): string {
    const digits = Math.floor(Math.random() * 10000)
        .toString()
        .padStart(4, "0");
    return `PR-2026-${digits}`;
}

// --- Wire-authoritative loyalty display (MOVMO-567) ---

const DEFAULT_TIER_LABEL = "Member";
const PREFERRED_PROGRAM_CODE = "mabuhay_miles";

export interface WireLoyaltyLink {
    programCode: string;
    airlineName: string;
    memberNumber: string;
    tier: string;
    milesBalance: number;
    verified: boolean;
}

/**
 * MOVMO-567: turns the `loyalty` group from the latest /me
 * response into the record the header chip / trip-selector render, or
 * `null` when the group is absent or empty (traveler hasn't granted
 * loyalty.read to PA, or has no programs on file) — the UNLINKED state,
 * regardless of any fixture/theater record elsewhere. Prefers the
 * `mabuhay_miles` program when present; falls back to the first program
 * otherwise (per Brian: "a mabuhay_miles (or any) program" counts as
 * linked). `tier_status`/`points` are `omitempty` on the wire, hence the
 * fallbacks — an unset tier or points balance is not the same as being
 * unlinked.
 */
export function deriveWireLoyaltyLink(
    loyalty?: DemoAirlineLoyaltyClaims | null,
): WireLoyaltyLink | null {
    const programs = loyalty?.programs ?? [];
    if (programs.length === 0) return null;
    const program =
        programs.find((p) => p.program_code === PREFERRED_PROGRAM_CODE) ??
        programs[0];
    return {
        programCode: program.program_code,
        airlineName: program.airline_name,
        memberNumber: program.member_number,
        tier: program.tier_status || DEFAULT_TIER_LABEL,
        milesBalance: program.points ?? 0,
        verified: program.verified,
    };
}

/**
 * MOVMO-542: builds the toast's LoyaltyLinkRecord from a REAL
 * WireLoyaltyLink (the just-linked response's loyalty group), not fixture
 * data — the modal calls this only when the wire confirms the link
 * (movmo.loyalty present), so the toast copy is truthful. `path`/
 * `importConsent` aren't on the wire shape — the modal knows them from
 * which action it just took (enroll vs. member-number+code) and the
 * single-checkbox consent disclosure, respectively.
 */
export function wireLinkToRecord(
    link: WireLoyaltyLink,
    path: LoyaltyLinkRecord["path"],
    importConsent: boolean,
): LoyaltyLinkRecord {
    return {
        memberNumber: link.memberNumber,
        tier: link.tier,
        milesBalance: link.milesBalance,
        linkedAt: new Date().toISOString(),
        path,
        importConsent,
    };
}

// --- Transition theater (MOVMO-541/567) ---
//
// One-shot sessionStorage flag: PaConnectCallback stashes the fixture
// record here right after a loyalty-in-consent OAuth redirect so the
// landing page can show the matching confirmation toast exactly once.
// THEATER ONLY (see file-header comment) — never read by
// useLoyaltySession.ts, only by LoyaltySyncToast's copy.

export const LOYALTY_TOAST_FLAG_KEY = "movmo_demo_loyalty_toast_pa";

export function writeToastFlag(record: LoyaltyLinkRecord): void {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(
        LOYALTY_TOAST_FLAG_KEY,
        JSON.stringify(record),
    );
}

/** Reads and clears the one-shot toast flag; malformed/missing = null. */
export function readAndClearToastFlag(): LoyaltyLinkRecord | null {
    if (typeof window === "undefined") return null;
    const raw = window.sessionStorage.getItem(LOYALTY_TOAST_FLAG_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(LOYALTY_TOAST_FLAG_KEY);
    try {
        return JSON.parse(raw) as LoyaltyLinkRecord;
    } catch {
        return null;
    }
}

// MOVMO-542 (denied-scope follow-up): a second, independent one-shot flag —
// set the moment the grant-half-of-late-link interstitial's connect
// ceremony is LAUNCHED (LoyaltyLinkModal.tsx, clicking "Turn on sharing"),
// read-and-cleared once by the landing page after the traveler lands back
// from the OAuth round trip. This exists because that redirect has two
// differently-shaped outcomes depending on the traveler's PRIOR decision
// for loyalty.read on this client (MOVMO-455):
//   - UNDECIDED -> auth-ui shows the delta-consent screen -> grant -> the
//     wire picks up `loyalty` on the next /me read -> chip shows. No
//     notice needed; deriveWireLoyaltyLink(traveler.loyalty) is non-null
//     by the time the flag is checked, so nothing fires.
//   - DENIED (traveler explicitly turned loyalty.read off for PA in their
//     Movmo hub) -> because this request names loyalty.read explicitly, a
//     prior denial is RE-ASKED — auth-ui shows the delta-consent screen
//     again with a "previously turned off" label instead of completing
//     silently (only a scope requested without naming it is skipped
//     without a prompt). If the traveler declines AGAIN on that re-ask,
//     the round trip still completes with no grant. Without this flag
//     that's a dead end: click, decline again, land back, header
//     unchanged, no explanation.
// The landing page (PhilippineAirlines.tsx) is the only reader — this
// flag is NOT the toast-flag pattern above and never touches
// useLoyaltySession.ts's wire-authoritative derivation.

export const LOYALTY_GRANT_ATTEMPT_FLAG_KEY =
    "movmo_demo_loyalty_grant_attempt_pa";

export function writeLoyaltyGrantAttemptFlag(): void {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(LOYALTY_GRANT_ATTEMPT_FLAG_KEY, "1");
}

/** Reads and clears the one-shot grant-attempt flag; true iff it was set. */
export function readAndClearLoyaltyGrantAttemptFlag(): boolean {
    if (typeof window === "undefined") return false;
    const wasSet =
        window.sessionStorage.getItem(LOYALTY_GRANT_ATTEMPT_FLAG_KEY) !==
        null;
    window.sessionStorage.removeItem(LOYALTY_GRANT_ATTEMPT_FLAG_KEY);
    return wasSet;
}

/**
 * MOVMO-542 (notice-race fix): pure decision extracted from
 * PhilippineAirlines.tsx's grant-attempt effect so it's testable without a
 * DOM/React harness. `flagSet` is whether the grant-attempt flag was
 * present (the ceremony was launched); `loyaltyPresent` is whether the
 * traveler's wire loyalty group is non-null AFTER the session read has settled
 * for this page load (see hooks/linkedTravelerStore.ts's
 * waitForLoyaltyGrant — never the mount-time cache, which can still
 * be racing the post-callback /me read). The notice is warranted
 * only when the ceremony ran and settled wire state still shows no grant.
 */
export function shouldShowLoyaltyScopeDeniedNotice(
    flagSet: boolean,
    loyaltyPresent: boolean,
): boolean {
    return flagSet && !loyaltyPresent;
}

/**
 * MOVMO-541 loyalty-in-consent: the Movmo consent screen (auth-ui) captures
 * the loyalty decision and returns it on the OAuth redirect as query params
 * (demo-only transport — real arch carries signed claims / S2S, MOVMO-542):
 *   loyalty_status = linked | joined
 *   loyalty_member_id = last 4 of the member number (linked only)
 *   loyalty_import = true | false  (airline→Movmo import consent)
 *   loyalty_program = mabuhay_miles (always sent; not read here yet)
 * Absent params → null (traveler skipped loyalty; plain connect).
 *
 * THEATER ONLY (MOVMO-567): the record this builds feeds the confirmation
 * toast's copy, never the resting linked/unlinked display — see file-header
 * comment.
 */
export function buildLinkRecordFromRedirect(
    params: URLSearchParams,
): LoyaltyLinkRecord | null {
    const status = params.get("loyalty_status");
    // Present-but-unknown is contract drift with auth-ui's emitter,
    // not a traveler choice — leave a breadcrumb instead of silently
    // degrading a real decision to "skipped".
    if (status && status !== "joined" && status !== "linked") {
        console.warn(
            "[pa] unknown loyalty_status, treating as skip:",
            status,
        );
        return null;
    }
    const importConsent = params.get("loyalty_import") === "true";
    if (status === "joined") {
        return {
            memberNumber: newEnrolledMemberNumber(),
            tier: "Member",
            milesBalance: 500,
            linkedAt: new Date().toISOString(),
            path: "enrolled",
            importConsent,
        };
    }
    if (status !== "linked") return null;
    const last4 = params.get("loyalty_member_id") ?? "";
    // The fixture member (ending 8842) is the email-match happy path; any
    // other last4 means the traveler typed a number in the airline-verified
    // fallback on the consent screen.
    const isFixture = last4 === "" || last4 === "8842";
    return {
        memberNumber: isFixture
            ? MOCK_LOYALTY_MEMBER.memberNumber
            : `PR-0000-${last4}`,
        tier: isFixture ? MOCK_LOYALTY_MEMBER.tier : "Member",
        milesBalance: isFixture ? MOCK_LOYALTY_MEMBER.milesBalance : 24680,
        linkedAt: new Date().toISOString(),
        path: "linked",
        importConsent,
    };
}
